"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { translate } from "../../utils/translator"
import { trackOncePerSession } from "../../PixelTrackers/trackingUtils"
import PortaledModal from "../Modals/PortaledModal"
import useIsMobile from "../../customHooks/useIsMobile"
import icon1 from "../../icons/CostBreakdown/icon1.png"
import icon2 from "../../icons/CostBreakdown/icon2.png"
import icon3 from "../../icons/CostBreakdown/icon3.png"
import icon4 from "../../icons/CostBreakdown/icon4.png"
import icon5 from "../../icons/CostBreakdown/icon5.png"
import kauferschutz from "../../icons/CostBreakdown/Käuferschutz.svg"
import kauferschutzMobile from "../../icons/CostBreakdown/Käuferschutz-nobile.svg"
import "./CostBreakDownModal.css"
import { ORDER_FLOWS, SWITZERLAND } from "../../utils/consts"

interface CostBreakdownModalProps {
  isOpen: boolean
  onClose: () => void
  language?: string
  initialSelectedMenuItem?: string
  country?: string
  flow?: string | null
  sampleType?: string | null
  isSelfPickup?: boolean
  isOnlyPrescription?: boolean
  isCannabis?: boolean
}

const CostBreakdownModal: React.FC<CostBreakdownModalProps> = ({ flow, isOpen, onClose, country='DE', language='de', initialSelectedMenuItem = "why-choose", sampleType, isSelfPickup, isOnlyPrescription, isCannabis }) => {
  const isTestLab = !!sampleType;
  // CH has its own test-lab copy under the `_ch` keys (DE keys stay untouched)
  const keySuffix = country?.toUpperCase() === SWITZERLAND ? `_${SWITZERLAND.toLowerCase()}` : '';
  // Cannabis keeps its €/g + cannabis-doctor copy; every other category gets the generic copy
  const cannabisSuffix = isCannabis ? '_cannabis' : ''
  const [selectedMenuItem, setSelectedMenuItem] = useState(initialSelectedMenuItem)
  const [animationState, setAnimationState] = useState<string>("")
  const isMobile = useIsMobile()
  const isSchengenFlow = flow === ORDER_FLOWS.SCHENGEN;
  useEffect(() => {
    setSelectedMenuItem(initialSelectedMenuItem)
  }, [initialSelectedMenuItem])

  // Handle animation state for mobile modal
  useEffect(() => {
    if (!animationState) {
      setAnimationState("hidden")
    } else if (isOpen && isMobile) {
      setAnimationState("animate-fade-in-up")
    } else if (isMobile) {
      setAnimationState("animate-fade-out-down")
    }
  }, [isOpen, isMobile])

  const handleClose = () => {
    if (isMobile) {
      setAnimationState("animate-fade-out-down")
      setTimeout(() => {
        setAnimationState("hidden")
        onClose()
      }, 500)
    } else {
      onClose()
    }
  }

  const trackFAQClick = (menuItemId: string) => {
    let questionText = ""
    
    switch (menuItemId) {
      case "why-choose":
        questionText = "Why do patients choose DoktorABC?"
        break
      case "service-fee":
        questionText = "Is the service fee new?"
        break
      case "total-amount":
        questionText = "Will my total amount increase?"
        break
      case "prescription-approval":
        questionText = "What if I'm not approved?"
        break
      case "self-pickup":
        questionText = "How do I know when I can pick up my order?"
        break
      case "test-lab-how-it-works":
        questionText = "How does the home test work?"
        break
      case "test-lab-results-time":
        questionText = "How long does it take to get my results?"
        break
      case "test-lab-what-to-do":
        questionText = "What to do when you receive your kit"
        break
      default:
        questionText = "Unknown FAQ question"
    }

    trackOncePerSession('custom', 'FAQ Question', {
      Question: questionText,
    })
  }

  const menuItems = isTestLab ? [
    {
      id: "test-lab-how-it-works",
      title: translate(language, "shipping", `CostBreakdown/test_lab_how_it_works_title${keySuffix}`),
    },
    {
      id: "test-lab-results-time",
      title: translate(language, "shipping", `CostBreakdown/test_lab_results_time_title${keySuffix}`),
    },
    {
      id: "test-lab-what-to-do",
      title: translate(language, "shipping", `CostBreakdown/test_lab_what_to_do_title${keySuffix}`),
    },
  ] : [
    {
      id: "why-choose",
      title: translate(language, "shipping", "CostBreakdown/why_choose_title"),
    },
    {
      id: "total-amount",
      title: translate(language, "shipping", "CostBreakdown/total_amount_title"),
    },
    ...(isSelfPickup ? [{
      id: "self-pickup",
      title: translate(language, "shipping", "CostBreakdown/self_pickup_title"),
    }] : []),
    ...(flow !== ORDER_FLOWS.OTC_ONLY && !isSchengenFlow ? [{
      id: "prescription-approval",
      title: translate(language, "shipping", "CostBreakdown/prescription_approval_title"),
    }] : [])
  ]

  const renderContent = () => {
    switch (selectedMenuItem) {
      case "why-choose":
        return (
          <div className="">
            <h2 className="content-title text-[20px] mb-5 text-center">{translate(language, "shipping", "CostBreakdown/why_choose_title")}</h2>

            <div className="feature-list">
              <div className="feature-item">
                <div className="feature-icon green">
                  <img src={icon1} alt="Pricing" />
                </div>
                <div className="feature-content">
                  <h3>{translate(language, "shipping", `CostBreakdown/clear_price_title${cannabisSuffix}`)}</h3>
                  <p>{translate(language, "shipping", `CostBreakdown/clear_price_description${cannabisSuffix}`)}</p>
                </div>
              </div>

              <div className="feature-item">
                <div className="feature-icon blue">
                  <img src={icon2} alt="Doctor" />
                </div>
                <div className="feature-content">
                  <h3>{translate(language, "shipping", `CostBreakdown/specialized_doctors_title${cannabisSuffix}`)}</h3>
                  <p>{translate(language, "shipping", `CostBreakdown/specialized_doctors_description${cannabisSuffix}`)}</p>
                </div>
              </div>

              <div className="feature-item">
                <div className="feature-icon orange">
                  <img src={icon3} alt="Delivery" />
                </div>
                <div className="feature-content">
                  <h3>{translate(language, "shipping", "CostBreakdown/discrete_delivery_title")}</h3>
                  <p>{translate(language, "shipping", "CostBreakdown/discrete_delivery_description")}</p>
                </div>
              </div>

              <div className="feature-item">
                <div className="feature-icon green">
                  <img src={icon4} alt="Security" />
                </div>
                <div className="feature-content">
                  <h3>{translate(language, "shipping", "CostBreakdown/gdpr_compliant_title")}</h3>
                  <p>{translate(language, "shipping", `CostBreakdown/gdpr_compliant_description_${country.toLowerCase()}`)}</p>
                </div>
              </div>

              <div className="feature-item">
                <div className="feature-icon purple">
                  <img src={icon5} alt="Support" />
                </div>
                <div className="feature-content">
                  <h3>{translate(language, "shipping", "CostBreakdown/human_support_title")}</h3>
                  <p>{translate(language, "shipping", "CostBreakdown/human_support_description")}</p>
                </div>
              </div>
            </div>

            <div className="statistics-section">
              <div className="statistic-box-large">
                <div className="statistic-item">
                  <div className="statistic-number">{translate(language, "shipping", "CostBreakdown/delivery_time")}</div>
                  <div className="statistic-label">{translate(language, "shipping", `CostBreakdown/delivery_time_label_${country.toLowerCase()}`)}</div>
                </div>
                <div className="statistic-item">
                  <div className="statistic-number">{translate(language, "shipping", "CostBreakdown/processed_orders")}</div>
                  <div className="statistic-label">{translate(language, "shipping", "CostBreakdown/processed_orders_label")}</div>
                </div>
                <div className="statistic-item">
                  <div className="statistic-number">{translate(language, "shipping", "CostBreakdown/discrete_packaging")}</div>
                  <div className="statistic-label">{translate(language, "shipping", "CostBreakdown/discrete_packaging_label")}</div>
                </div>
              </div>
            </div>
          </div>
        )

      case "service-fee":
        return (
          <div className="content-space">
            <h2 className="content-title">{translate(language, "shipping", "CostBreakdown/service_fee_title")}</h2>
            <p className="content-text">
              {translate(language, "shipping", "CostBreakdown/service_fee_description")}
            </p>
          </div>
        )

      case "total-amount":
        return (
          <div className="content-space">
            <h2 className="content-title">{translate(language, "shipping", "CostBreakdown/total_amount_title")}</h2>
            <p className="content-text">
              {isOnlyPrescription
                ? translate(language, "shipping", "CostBreakdown/total_amount_description_prescription_only")
                : translate(language, "shipping", "CostBreakdown/total_amount_description")}
            </p>
          </div>
        )

      case "prescription-approval":
        return (
          <div className="content-space">
            <h2 className="content-title">{translate(language, "shipping", "CostBreakdown/prescription_approval_title")}</h2>
            <p className="content-text">
              {translate(language, "shipping", "CostBreakdown/prescription_approval_description")}
            </p>
          </div>
        )

      case "self-pickup":
        return (
          <div className="content-space">
            <h2 className="content-title">{translate(language, "shipping", "CostBreakdown/self_pickup_title")}</h2>
            <p className="content-text">
              {translate(language, "shipping", "CostBreakdown/self_pickup_description")}
            </p>
          </div>
        )

      case "test-lab-how-it-works":
        return (
          <div className="content-space">
            <h2 className="content-title">{translate(language, "shipping", `CostBreakdown/test_lab_how_it_works_title${keySuffix}`)}</h2>
            <p className="content-text">
              {translate(language, "shipping", `CostBreakdown/test_lab_how_it_works_description_${sampleType || 'blood'}${keySuffix}`) ||
               translate(language, "shipping", `CostBreakdown/test_lab_how_it_works_description${keySuffix}`)}
            </p>
          </div>
        )

      case "test-lab-results-time":
        return (
          <div className="content-space">
            <h2 className="content-title">{translate(language, "shipping", `CostBreakdown/test_lab_results_time_title${keySuffix}`)}</h2>
            <p className="content-text">
              {translate(language, "shipping", `CostBreakdown/test_lab_results_time_description${keySuffix}`)}
            </p>
          </div>
        )

      case "test-lab-what-to-do":
        return (
          <div className="content-space">
            <h2 className="content-title">{translate(language, "shipping", `CostBreakdown/test_lab_what_to_do_title${keySuffix}`)}</h2>
            {/* whitespace-pre-line: the CH copy is one step per line (\n in the translation) */}
            <p className="content-text whitespace-pre-line">
              {translate(language, "shipping", `CostBreakdown/test_lab_what_to_do_description${keySuffix}`)}
            </p>
          </div>
        )

      default:
        return null
    }
  }

  return (
    <>
      {/* Mobile Modal */}
      {isMobile && (
        <PortaledModal
          shouldShowModal={isOpen}
          hideModal={handleClose}
          shouldBackgroundClickHideModal={true}
          backgroundColor="rgba(0, 0, 0, 0.5)"
        >
          <div 
            id="cost-breakdown-mobile-modal" 
            className={`w-full max-h-[80vh] bg-white rounded-[30px_30px_0px_0px] shadow-2xl pt-0 flex flex-col overflow-hidden fixed bottom-0 left-0 right-0 ${animationState ? animationState : "hidden"}`}
          >
            {/* Mobile top scroller */}
            <div 
              className="w-full flex justify-center pt-3 pb-2 cursor-pointer"
              onTouchStart={(e) => {
                const startY = e.touches[0].clientY;
                e.currentTarget.dataset.startY = startY.toString();
              }}
              onTouchMove={(e) => {
                const startY = parseFloat(e.currentTarget.dataset.startY || '0');
                const currentY = e.touches[0].clientY;
                const deltaY = currentY - startY;
                
                if (deltaY > 50) { // Threshold for swipe down
                  handleClose();
                }
              }}
            >
              <div className="w-24 h-1.5 bg-[#EDEDED] rounded-full" />
            </div>

            {/* Scrollable content */}
            <div className="flex-1 overflow-y-auto custom-scrollbar">
              {/* Header */}
              <div className="relative flex flex-col items-center justify-center pb-2 px-6">
                <div className="w-full flex flex-row items-center justify-center">
                  <div className="bg-[#0A9281] max-w-[300px] text-center text-white font-medium text-[14px] px-4 py-2 rounded-b-3xl tracking-wide">
                    {translate(language, "shipping", "CostBreakdown/faq_header")}
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="px-4 md:px-6">
                {renderContent()}
              </div>

              {/* Footer with Käuferschutz */}
              {!isTestLab && (
                <div className="px-4 md:px-6 pb-10">
                  <div className="flex items-center gap-2">
                    <img src={kauferschutzMobile} alt="Käuferschutz" className="cost-bottom-image" />
                    <div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </PortaledModal>
      )}

      {/* Desktop Modal */}
      {!isMobile && (
        <PortaledModal
          shouldShowModal={isOpen}
          hideModal={handleClose}
          shouldBackgroundClickHideModal={true}
          backgroundColor="rgba(0, 0, 0, 0.5)"
        >
          <div className="modal-container cost-breakdown-modal">
            {/* Header with close button */}
            <div className="modal-header">
              <button onClick={handleClose} className="close-button" aria-label="Close modal">
                <svg className="close-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="modal-body">
              {/* Left Sidebar Menu */}
              <div className="sidebar">
                <div className="menu-items">
                  {menuItems.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => {
                        setSelectedMenuItem(item.id)
                        trackFAQClick(item.id)
                      }}
                      className={`menu-item ${selectedMenuItem === item.id ? "active" : ""}`}
                    >
                      <span className="menu-item-text">{item.title}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Right Content Area */}
              <div className="content-area">{renderContent()}</div>
            </div>

            <div className="modal-footer">
              {!isTestLab && (
                <img src={kauferschutz} alt="Käuferschutz" className="kauferschutz-icon" />
              )}
            </div>
          </div>
        </PortaledModal>
      )}
    </>
  )
}

export default CostBreakdownModal
